import styled, { css } from "styled-components";
import { InputSpinner, InputSpinnerStyles } from "@ddm-design-system/input-spinner";
import { Tone } from "./helpers";

export const Section = styled.section(
  ({ theme }) => css`
    border-top: 1px solid ${theme.colors.grey.grey25};
    padding: ${theme.spaceUnit.lg} 0;
  `
);

export const SectionHeader = styled.div(
  ({ theme }) => css`
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: ${theme.spaceUnit.xs};
    margin-bottom: ${theme.spaceUnit.xs};
  `
);

export const Help = styled.p(
  ({ theme }) => css`
    margin: 0 0 ${theme.spaceUnit.md};
    color: ${theme.colors.grey.grey100};
    font-size: 14px;
    line-height: 20px;
    max-width: 720px;
  `
);

const toneColors = (theme: any, tone: Tone) => {
  switch (tone) {
    case "success":
      return { background: theme.colors.success.success25, color: theme.colors.success.success100 };
    case "warning":
      return { background: theme.colors.warning.warning25, color: theme.colors.black };
    case "alert":
      return { background: theme.colors.alert.alert25, color: theme.colors.alert.alert100 };
    default:
      return { background: theme.colors.grey.grey25, color: theme.colors.black };
  }
};

export const Chip = styled.span<{ tone: Tone }>(({ theme, tone }) => {
  const { background, color } = toneColors(theme, tone);
  return css`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    border-radius: 12px;
    padding: 2px 10px;
    font-size: 13px;
    line-height: 20px;
    background: ${background};
    color: ${color};
  `;
});

export const Dot = styled.span<{ tone: Tone }>(({ theme, tone }) => {
  const { color } = toneColors(theme, tone);
  return css`
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: ${tone === "warning" ? theme.colors.warning.warning100 : color};
  `;
});

export const Banner = styled.div<{ tone: Tone }>(({ theme, tone }) => {
  const { background } = toneColors(theme, tone);
  return css`
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: ${theme.spaceUnit.sm};
    padding: ${theme.spaceUnit.sm} ${theme.spaceUnit.md};
    border-radius: 10px;
    background: ${background};
    margin-bottom: ${theme.spaceUnit.md};
    font-size: 14px;
  `;
});

export const ControlRow = styled.div(
  ({ theme }) => css`
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: ${theme.spaceUnit.md};
  `
);

export const Slider = styled.input.attrs({ type: "range" })(
  ({ theme }) => css`
    flex: 1;
    min-width: 200px;
    max-width: 480px;
    accent-color: ${theme.colors.black};
    cursor: pointer;

    &:disabled {
      cursor: not-allowed;
      opacity: 0.5;
    }
  `
);

export const StepButton = styled.button(
  ({ theme }) => css`
    width: 36px;
    height: 36px;
    border-radius: 50%;
    border: 1px solid ${theme.colors.grey.grey50};
    background: ${theme.colors.white};
    font-size: 18px;
    line-height: 1;
    cursor: pointer;

    &:disabled {
      cursor: not-allowed;
      opacity: 0.5;
    }
  `
);

export const Value = styled.span<{ changed?: boolean }>(
  ({ changed }) => css`
    min-width: 84px;
    font-size: 18px;
    font-weight: ${changed ? 700 : 400};
    font-variant-numeric: tabular-nums;
  `
);

export const Meta = styled.div(
  ({ theme }) => css`
    color: ${theme.colors.grey.grey100};
    font-size: 13px;
    margin-top: ${theme.spaceUnit.xs};
  `
);

export const ApplyBar = styled.div(
  ({ theme }) => css`
    position: sticky;
    bottom: 0;
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: flex-end;
    gap: ${theme.spaceUnit.md};
    padding: ${theme.spaceUnit.md} 0;
    background: ${theme.colors.white};
    border-top: 1px solid ${theme.colors.grey.grey25};
  `
);

export const HistoryTable = styled.table(
  ({ theme }) => css`
    width: 100%;
    border-collapse: collapse;
    font-size: 14px;

    th,
    td {
      text-align: left;
      vertical-align: top;
      padding: ${theme.spaceUnit.xs} ${theme.spaceUnit.sm} ${theme.spaceUnit.xs} 0;
      border-bottom: 1px solid ${theme.colors.grey.grey25};
    }

    th {
      color: ${theme.colors.grey.grey100};
      font-weight: 400;
    }
  `
);

/** Same treatment as the CE location form: the design-system spinner input is otherwise squeezed. */
export const VolumeSpinner = styled(InputSpinner)`
  width: 180px;

  ${InputSpinnerStyles.Value} {
    width: auto !important;
    text-align: center;
  }
`;
